import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiArrowLeft, FiSearch } from "react-icons/fi";
import { supabase } from "../../supabase/supabase";
import { useAuth } from "../../contexts/AuthContext";
import "./SendSearch.css";

const DEFAULT_AVATAR =
  "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIHZpZXdCb3g9IjAgMCA0OCA0OCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjQiIGN5PSIyNCIgcj0iMjQiIGZpbGw9IiNmM2Y0ZjYiLz4KPHN2ZyB4PSIxMiIgeT0iMTIiIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM5Y2EzYWYiIHN0cm9rZS13aWR0aD0iMiIgc3Rya2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiPgo8cGF0aCBkPSJNMjAgMjF2LTJhNCA0IDAgMCAwLTQtNEg4YTQgNCAwIDAgMC00IDR2MiIvPgo8Y2lyY2xlIGN4PSIxMiIgY3k9IjciIHI9IjQiLz4KPC9zdmc+Cjwvc3ZnPg==";

function resolveAvatarUrl(userId, avatarFilename) {
  if (!avatarFilename) return DEFAULT_AVATAR;
  if (typeof avatarFilename === "string" && avatarFilename.startsWith("data:")) return avatarFilename;
  if (typeof avatarFilename === "string" && /^https?:\/\//i.test(avatarFilename)) return avatarFilename;
  try {
    const { data } = supabase.storage.from("avatars").getPublicUrl(`${userId}/${avatarFilename}`);
    return data?.publicUrl || DEFAULT_AVATAR;
  } catch {
    return DEFAULT_AVATAR;
  }
}

export default function SendSearch() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [results, setResults] = useState([]);

  const debounceRef = useRef(null);

  const trimmed = useMemo(() => query.trim(), [query]);

  const runSearch = useCallback(
    async (q) => {
      const term = (q || "").trim();
      if (!term) {
        setResults([]);
        setError("");
        return;
      }

      setLoading(true);
      setError("");
      try {
        const like = `%${term}%`;
        const { data, error: supaErr } = await supabase
          .from("profiles")
          .select("user_id, username, display_name, avatar_url")
          .or(`username.ilike.${like},display_name.ilike.${like}`)
          .limit(25);

        if (supaErr) throw supaErr;

        const filtered = (data || [])
          .filter((p) => p?.user_id && p.user_id !== currentUser?.id)
          .map((p) => ({
            user_id: p.user_id,
            username: p.username || "user",
            display_name: p.display_name || p.username || "User",
            avatar: resolveAvatarUrl(p.user_id, p.avatar_url),
          }));

        setResults(filtered);
      } catch (e) {
        console.error("[SendSearch] search error:", e);
        setError("Failed to search users. Please try again.");
        setResults([]);
      } finally {
        setLoading(false);
      }
    },
    [currentUser?.id]
  );

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      runSearch(trimmed);
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [trimmed, runSearch]);

  return (
    <div className="send-search-page">
      <div className="send-search-shell">
        <div className="send-search-topbar">
          <button className="send-back" onClick={() => navigate(-1)} aria-label="Back">
            <FiArrowLeft />
          </button>
          <h1 className="send-search-title">Transfer Coins</h1>
          <div className="send-search-spacer" />
        </div>

        <div className="send-search-body">
          <h2 className="send-search-h2">Search for a user</h2>
          <p className="send-search-sub">
            Enter username or display name to find the recipient
          </p>

          <div className="send-search-inputWrap">
            <FiSearch className="send-search-icon" aria-hidden="true" />
            <input
              className="send-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search users..."
              autoFocus
              spellCheck={false}
              autoComplete="off"
            />
          </div>

          {error && <div className="send-search-error">{error}</div>}

          <div className="send-search-results">
            {loading ? (
              <div className="send-search-loading">Searching…</div>
            ) : trimmed.length === 0 ? (
              <div className="send-search-empty">Start typing to search.</div>
            ) : results.length === 0 ? (
              <div className="send-search-empty">No users found.</div>
            ) : (
              results.map((u) => (
                <button
                  key={u.user_id}
                  className="send-search-row"
                  onClick={() => navigate(`/sending?to=${encodeURIComponent(u.user_id)}`)}
                >
                  <img className="send-search-avatar" src={u.avatar} alt={u.display_name} />
                  <div className="send-search-meta">
                    <div className="send-search-name">{u.display_name}</div>
                    <div className="send-search-username">@{u.username}</div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

